import { collection, getDocs, query, where, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';

/**
 * Service to handle practice data lookup and management
 */
export const practiceService = {
  /**
   * Get a practice by its ID - first checks user's selected practices, 
   * then browser local storage caches, exactly like PracticeSelector
   * 
   * @param {string} practiceId - The practice ID to look up
   * @param {Array} userSelectedPractices - User's currently selected practices (in-memory)
   * @param {string} userId - Optional user ID for additional context
   * @returns {Promise<Object>} - The practice data or null if not found
   */
  async getPracticeById(practiceId, userSelectedPractices = [], userId = null) {
    try {
      // Log the search starting
      console.log(`🔍 Looking up practice with ID: ${practiceId}`);
      console.log(`🔍 User has ${userSelectedPractices?.length || 0} selected practices`);
      
      // STEP 1: First check the user's selected practices list (in-memory)
      if (userSelectedPractices && userSelectedPractices.length > 0) {
        // Try multiple fields for matching to be more flexible
        const practice = userSelectedPractices.find(p => 
          // Check all possible ID fields
          p.prac_code === practiceId || 
          p.id === practiceId ||
          p.practiceId === practiceId
        );
        
        if (practice) {
          console.log(`✅ Practice ${practiceId} found in user's selected practices (memory)`);
          return practice;
        }
      }
      
      // STEP 2: Check browser's localStorage for practices_data
      try {
        console.log(`🔍 Checking browser local storage for practices_data`);
        
        // Get data from localStorage - IMPORTANT: It's an object with practice codes as keys, not an array
        const practicesJson = localStorage.getItem('practices_data');
        if (practicesJson) {
          try {
            // Parse the JSON string into an object
            const practicesMap = JSON.parse(practicesJson);
            
            console.log(`🔍 Found practices data in localStorage`);
            
            // DIRECT LOOKUP: Since it's an object with practice codes as keys, we can directly access it
            if (practicesMap[practiceId]) {
              // We found the practice directly by its code
              console.log(`✅ Practice ${practiceId} found directly in localStorage map`);
              const practice = practicesMap[practiceId];
              
              // Make sure it has a prac_code
              if (!practice.prac_code) {
                practice.prac_code = practiceId;
              }
              
              return practice;
            }
            
            // If not found by direct key lookup, try searching through all values
            // First convert to array of practices
            const practicesArray = Object.values(practicesMap);
            console.log(`🔍 Checking ${practicesArray.length} practices in localStorage data`);
            
            // Now search through the values
            const practice = practicesArray.find(p => 
              p && (p.prac_code === practiceId || p.id === practiceId || p.practiceId === practiceId)
            );
            
            if (practice) {
              console.log(`✅ Practice ${practiceId} found in localStorage values`);
              return practice;
            }
          } catch (parseError) {
            console.warn(`⚠️ Error parsing practices data from localStorage:`, parseError);
          }
        } else {
          console.log(`⚠️ No practices_data found in localStorage`);
        }
      } catch (storageError) {
        console.warn(`⚠️ Error accessing local storage:`, storageError);
      }
      
      // STEP 3: Try browser application cache
      try {
        console.log(`🔍 Checking browser application cache data`);
        if (window.caches) {
          try {
            const cache = await window.caches.open('app-v1');
            const cachedResponse = await cache.match('practices_data');
            if (cachedResponse) {
              // This might also be an object with prac_code keys, not an array
              const practicesData = await cachedResponse.json();
              
              // Check if it's direct key access object
              if (practicesData[practiceId]) {
                console.log(`✅ Practice ${practiceId} found directly in browser cache`);
                const practice = practicesData[practiceId];
                
                // Make sure it has a prac_code
                if (!practice.prac_code) {
                  practice.prac_code = practiceId;
                }
                
                return practice;
              }
              
              // Otherwise try as array of practices
              if (Array.isArray(practicesData)) {
                console.log(`🔍 Checking ${practicesData.length} practices in browser cache`);
                
                const practice = practicesData.find(p => 
                  p.prac_code === practiceId || 
                  p.id === practiceId || 
                  p.practiceId === practiceId
                );
                
                if (practice) {
                  console.log(`✅ Practice ${practiceId} found in browser cache`);
                  return practice;
                }
              } else {
                // Try as object with values
                const practicesArray = Object.values(practicesData);
                
                const practice = practicesArray.find(p => 
                  p && (p.prac_code === practiceId || p.id === practiceId || p.practiceId === practiceId)
                );
                
                if (practice) {
                  console.log(`✅ Practice ${practiceId} found in browser cache values`);
                  return practice;
                }
              }
            }
          } catch (cacheError) {
            console.warn(`⚠️ Error accessing browser cache:`, cacheError);
          }
        }
      } catch (cacheError) {
        console.warn(`⚠️ Error with browser cache:`, cacheError);
      }
      
      // STEP 4: Last resort - check Firestore for the user's practice document
      if (userId) {
        console.log(`🔍 Practice not found locally, checking user document in Firestore...`);
        try {
          // Try to find if the practice is in the user's document
          const userDoc = await getDoc(doc(db, 'users', userId));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            if (userData.allPractices && Array.isArray(userData.allPractices)) {
              console.log(`🔍 User document contains ${userData.allPractices.length} practices`);
              
              const practice = userData.allPractices.find(p => 
                (typeof p === 'object' ? 
                  (p.prac_code === practiceId || p.id === practiceId || p.practiceId === practiceId) : 
                  p === practiceId)
              );
              
              if (practice) {
                console.log(`✅ Practice ${practiceId} found in user's Firestore document`);
                return typeof practice === 'object' ? practice : { prac_code: practice };
              }
            }
          }
        } catch (firestoreError) {
          console.warn(`⚠️ Error checking user document:`, firestoreError);
        }
      }
      
      // Log error - we can't find the practice anywhere
      console.error(`❌ Practice with ID ${practiceId} not found in any source`);
      
      // Return null to indicate practice not found - let calling code handle the error
      return null;
    } catch (error) {
      console.error('❌ Error getting practice by ID:', error);
      throw error; // Let the calling code handle the error
    }
  },
  
  /**
   * Get all practices from local storage first, falling back to download
   * @returns {Promise<Array>} - Array of all practices
   */
  async getAllPractices() {
    try {
      // First try to get practices from localStorage
      try {
        const storedPractices = localStorage.getItem('practices_data');
        if (storedPractices) {
          const practicesData = JSON.parse(storedPractices);
          
          // Handle both array and object with keys formats
          let practices = [];
          
          if (Array.isArray(practicesData)) {
            practices = practicesData;
          } else {
            // Convert from object with keys to array
            practices = Object.values(practicesData);
          }
          
          if (practices.length > 0) {
            console.log(`✅ Retrieved ${practices.length} practices from localStorage`);
            return practices;
          }
        }
      } catch (storageError) {
        console.warn('⚠️ Error retrieving practices from localStorage:', storageError);
      }
      
      // If not in local storage, try to get from browser cache
      if (window.caches) {
        try {
          const cache = await window.caches.open('app-v1');
          const cachedResponse = await cache.match('practices_data');
          if (cachedResponse) {
            const practicesData = await cachedResponse.json();
            
            // Handle both array and object with keys formats
            let practices = [];
            
            if (Array.isArray(practicesData)) {
              practices = practicesData;
            } else {
              // Convert from object with keys to array
              practices = Object.values(practicesData);
            }
            
            if (practices.length > 0) {
              console.log(`✅ Retrieved ${practices.length} practices from browser cache`);
              return practices;
            }
          }
        } catch (cacheError) {
          console.warn('⚠️ Error retrieving practices from browser cache:', cacheError);
        }
      }
      
      // As a last resort, query Firestore for practices
      console.log('🔍 Fetching practices from Firestore...');
      const snapshot = await getDocs(collection(db, 'practices_data'));
      
      const practices = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Cache the results in localStorage for future use
      try {
        localStorage.setItem('practices_data', JSON.stringify(practices));
        console.log(`✅ Cached ${practices.length} practices in localStorage`);
      } catch (cacheError) {
        console.warn('⚠️ Failed to cache practices in localStorage:', cacheError);
      }
      
      return practices;
    } catch (error) {
      console.error('❌ Error getting all practices:', error);
      throw error;
    }
  }
};
